import { useCallback, useEffect, useState } from "react";
import type {
  HomeNowCandidate,
  HomeNowResponse,
} from "@care-by-ella/contracts";
import { HOME_NOW_TIERS } from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { fetchHomeNowPreview, UnauthorizedError } from "@/lib/clinician-api";

// TES-363 — the reviewer's answer to "why isn't my authored item showing?":
// renders exactly what the patient's §2 ("Vad händer just nu") resolves to
// RIGHT NOW via the real HomeNowService ladder — the winning candidate + the
// calm fallback. The tier/type chip names which producer won.

const TIER_LABEL = new Map(
  HOME_NOW_TIERS.map((t) => [t.candidateType, `Nivå ${t.tier} · ${t.label}`]),
);

function CandidateCard({
  candidate,
  variant,
}: {
  candidate: HomeNowCandidate;
  variant: "winner" | "fallback";
}) {
  return (
    <div
      className={`rounded-md border p-md flex flex-col gap-xs ${
        variant === "winner"
          ? "border-brand/40 bg-brand/5"
          : "border-surfaceMuted bg-surfaceMuted/20"
      }`}
    >
      <div className="flex flex-wrap items-center gap-xs">
        <span
          className={`inline-flex items-center px-sm py-xs rounded-pill text-xs font-medium border ${
            variant === "winner"
              ? "bg-brand/10 text-brand border-brand/30"
              : "bg-surfaceMuted text-textSecondary border-surfaceMuted"
          }`}
        >
          {TIER_LABEL.get(candidate.candidateType) ?? candidate.candidateType}
        </span>
        {candidate.required ? (
          <span className="inline-flex items-center px-sm py-xs rounded-pill text-xs font-medium border bg-warning/10 text-warning border-warning/30">
            Krävs
          </span>
        ) : null}
        {candidate.blocksNextState ? (
          <span className="inline-flex items-center px-sm py-xs rounded-pill text-xs font-medium border bg-danger/10 text-danger border-danger/30">
            Blockerande
          </span>
        ) : null}
        <span className="text-xs text-textSecondary ml-auto">
          prioritet {candidate.priority}
          {candidate.expiresAt
            ? ` · till ${candidate.expiresAt.slice(0, 10)}`
            : ""}
        </span>
      </div>
      <p className="text-sm font-medium">{candidate.title}</p>
      <p className="text-sm text-textSecondary">{candidate.body}</p>
      {candidate.ctaLabel ? (
        <p className="text-xs">
          <span className="inline-block rounded-md bg-brand text-white px-sm py-xs">
            {candidate.ctaLabel}
          </span>
          {candidate.ctaPath ? (
            <code className="ml-sm text-textSecondary">
              {candidate.ctaPath}
            </code>
          ) : null}
        </p>
      ) : null}
      {candidate.sourceFlowKey ? (
        <p className="text-xs text-textSecondary">
          Källa: flöde <code>{candidate.sourceFlowKey}</code>
        </p>
      ) : null}
    </div>
  );
}

export function PatientHomeNow({
  patientId,
  locale,
}: {
  patientId: string;
  locale: "sv" | "en";
}) {
  const { signOut } = useAuth();
  const [data, setData] = useState<HomeNowResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchHomeNowPreview(patientId, locale)
      .then(setData)
      .catch((e) => {
        if (e instanceof UnauthorizedError) {
          void signOut();
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => setLoading(false));
  }, [patientId, locale, signOut]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-sm">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg">Hem just nu</h2>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-xs text-brand hover:underline disabled:opacity-50"
        >
          {loading ? "Hämtar…" : "Uppdatera"}
        </button>
      </div>
      <p className="text-xs text-textSecondary">
        Det prioriterade objekt (§2) patienten ser på hemskärmen just nu, valt
        av den riktiga prioriteringsstegen — samma svar som appen får. Språk:{" "}
        {locale.toUpperCase()}.
      </p>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-sm text-sm">
          {error}
        </div>
      ) : null}

      {data ? (
        <div className="flex flex-col gap-sm">
          {data.candidate ? (
            <CandidateCard candidate={data.candidate} variant="winner" />
          ) : (
            <p className="text-sm text-textSecondary">
              Ingen kandidat vinner just nu
              {data.fallback
                ? " — patienten ser reservinnehållet nedan."
                : " — sektionen är tom för patienten."}
            </p>
          )}
          {data.fallback ? (
            <div className="flex flex-col gap-xs">
              <span className="text-xs uppercase tracking-wide text-textSecondary">
                Reserv (visas när inget kräver uppmärksamhet)
              </span>
              <CandidateCard candidate={data.fallback} variant="fallback" />
            </div>
          ) : null}
        </div>
      ) : !error && loading ? (
        <p className="text-sm text-textSecondary">Hämtar…</p>
      ) : null}
    </section>
  );
}
